import { Load } from "@/components/load";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { useGetLastCashBalanceByStoreQuery } from "@/services/cash-balance";
import { useGenerateTermMutation } from "@/services/generate-term/generate-term";
import { useGetStoresByRegionalQuery } from "@/services/store/select-options";
import { applyCpfMaskIfNeeded } from "@/utils/cpf-mask";
import { isValidCPF } from "@/utils/format-cps";
import { zodResolver } from "@hookform/resolvers/zod";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";
import { ChevronLeft } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { reasonOptions } from "../data/options";
import { generateTermSchema } from "../data/schema";
import type { GenerateTermFormData } from "../data/types";

export const CreateTerm = () => {
  const navigate = useNavigate();
  const [generateTerm, { isLoading }] = useGenerateTermMutation();
  const { data: storesData, error: storesError } =
    useGetStoresByRegionalQuery();

  const storeOptions =
    storesData?.lojas.map((loja) => ({
      value: loja.toString(),
      label: `Loja ${loja}`,
    })) || [];

  useEffect(() => {
    if (!storesError) {
      return;
    }

    const errorData = (
      storesError as { data?: { messages?: string[]; message?: string } }
    )?.data;
    const errorMessage =
      errorData?.messages?.[0] ||
      errorData?.message ||
      "Erro ao buscar as lojas. Tente novamente.";

    toast({
      description: errorMessage,
      variant: "destructive",
    });
  }, [storesError]);

  const form = useForm<GenerateTermFormData>({
    resolver: zodResolver(generateTermSchema),
    defaultValues: {
      hasBalanceDiscrepancy: false,
      justification: "",
      treasuryAuditDate: new Date(),
    },
  });

  const selectedStoreId = form.watch("storeCode");

  const {
    data: cashBalanceByStore,
    error: cashBalanceError,
    isFetching: isFetchingCashBalance,
  } = useGetLastCashBalanceByStoreQuery(
    { storeId: selectedStoreId ? parseInt(selectedStoreId) : 0 },
    { skip: !selectedStoreId }
  );

  useEffect(() => {
    if (cashBalanceError) {
      form.setValue("cashBalance", "");
      form.setValue("treasuryAuditDate", new Date());
      form.setValue("balanceRequestDate", "");
      form.setValue("systemBalance", 0);

      toast({
        variant: "destructive",
        title: "Procura de Saldo",
        description:
          "Nenhum saldo foi emitido para esta loja. Por favor, emita o relatório de saldo antes de solicitar o termo.",
      });
      return;
    }

    if (cashBalanceByStore) {
      form.setValue(
        "treasuryAuditDate",
        new Date(cashBalanceByStore.created_at)
      );
      form.setValue(
        "cashBalance",
        new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(Number(cashBalanceByStore.previous_balance || 0))
      );
      form.setValue("systemBalance", cashBalanceByStore.daily_closing || 0);
      if (cashBalanceByStore.new_responsible_cpf) {
        form.setValue(
          "cpf",
          applyCpfMaskIfNeeded(cashBalanceByStore.new_responsible_cpf)
        );
      }
    }
  }, [cashBalanceByStore, cashBalanceError, form]);

  const onSubmit = async (data: GenerateTermFormData) => {
    try {
      if (
        !(data.treasuryAuditDate instanceof Date) ||
        isNaN(data.treasuryAuditDate.getTime())
      ) {
        console.error("Data inválida detectada:", data.treasuryAuditDate);
        toast({
          variant: "destructive",
          title: "Data inválida",
          description:
            "A data de auditoria do tesouro é inválida. Por favor, selecione uma data válida.",
        });
        return;
      }

      const formattedDate = data.treasuryAuditDate.toISOString().split("T")[0];

      const response = await generateTerm({
        cpf: data.cpf.replace(/\D/g, ""),
        storeCode: data.storeCode,
        data: {
          reason: data.reason,
          treasuryAuditDate: formattedDate,
          hasBalanceDiscrepancy: data.hasBalanceDiscrepancy,
          cashBalance: Number(data.cashBalance.replace(/\D/g, "")) / 100,
          justification: data.justification || "",
        },
        file: data.file,
      });

      if ("error" in response) {
        throw response.error;
      }

      if (!response.data) {
        throw new Error("Falha na geração do PDF: Dados não retornados");
      }

      toast({
        variant: "success",
        title: "Termo solicitado com sucesso!",
        description:
          "O termo foi criado e enviado para análise do time Financeiro.",
      });
      console.log(response.data);
      navigate(-1);
    } catch (error: Error | unknown) {
      console.error("Erro completo:", error);
      console.log("Estrutura do erro:", JSON.stringify(error, null, 2));

      const err = error as {
        data?:
          | string
          | {
              messages?: string[];
              message?: string;
            };
        message: string;
        status?: number;
      };

      let parsedData;
      try {
        if (typeof err.data === "string") {
          parsedData = JSON.parse(err.data);
        } else {
          parsedData = err.data;
        }
      } catch (e) {
        parsedData = { messages: [err.message] };
      }

      const messages = parsedData?.messages || [];
      const errorMessage =
        messages[0] || parsedData?.message || err.message || "";

      console.log("Mensagem de erro processada:", errorMessage);

      if (errorMessage.includes("Cargo não autorizado")) {
        toast({
          variant: "destructive",
          title: "Cargo não autorizado",
          description:
            "Termos podem ser gerados apenas para cargos autorizados.",
        });
      } else if (
        errorMessage.includes("não existe") ||
        errorMessage.includes("Usuário não existe") ||
        errorMessage.includes("não encontrado")
      ) {
        toast({
          variant: "destructive",
          title: "CPF não encontrado",
          description:
            "O CPF informado não está cadastrado no sistema. Verifique se digitou corretamente.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Erro ao solicitar termo",
          description:
            errorMessage || "Ocorreu um erro ao tentar solicitar o termo.",
        });
      }
    }
  };

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <Load />
        </div>
      )}

      <div className="flex flex-col min-h-screen">
        <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-6 md:px-6 md:py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="rounded-full h-10 w-10 p-0 shadow-sm hover:shadow transition-all"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-800">
                  Solicitar Novo Termo
                </h2>
                <Badge className="bg-primary/90 hover:bg-primary text-white px-2 py-0.5 text-xs">
                  Novo
                </Badge>
              </div>
            </div>
          </div>

          <main className="space-y-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <Card className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardHeader className="bg-white border-b pb-3">
                    <CardTitle className="text-xl text-gray-800">
                      Informações do Termo
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="cpf"
                        render={({ field: { onChange, ...field } }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-600">
                              CPF
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="000.000.000-00"
                                {...field}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, "");
                                  let formattedValue = value;
                                  
                                  if (value.length > 0) {
                                    formattedValue = value.replace(
                                      /(\d{3})(\d{3})(\d{3})(\d{2})/,
                                      "$1.$2.$3-$4"
                                    );
                                  }
                                  
                                  form.clearErrors('cpf');
                                  
                                  if (value.length === 11) {
                                    if (!isValidCPF(value)) {
                                      form.setError('cpf', { message: 'CPF inválido' });
                                    }
                                  }
                                  
                                  onChange(formattedValue);
                                }}
                                maxLength={14}
                                value={field.value}
                                className="border-gray-200 focus-visible:ring-primary/20"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="storeCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-600">
                              Loja
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="border-gray-200 focus-visible:ring-primary/20">
                                  <SelectValue placeholder="Selecionar loja" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {storeOptions.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="reason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-600">
                            Motivo
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="border-gray-200 focus-visible:ring-primary/20">
                                <SelectValue placeholder="Selecione o motivo" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {reasonOptions.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="treasuryAuditDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-600">
                              Data da Auditoria
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                {...field}
                                value={
                                  field.value
                                    ? new Date(field.value)
                                        .toISOString()
                                        .split("T")[0]
                                    : ""
                                }
                                max={new Date().toISOString().split("T")[0]}
                                onChange={(e) =>
                                  field.onChange(new Date(e.target.value))
                                }
                                className="border-gray-200 focus-visible:ring-primary/20"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="cashBalance"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-600">
                              Saldo em Caixa
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /\D/g,
                                    ""
                                  );
                                  if (value === "") {
                                    field.onChange("");
                                    return;
                                  }
                                  const formattedValue = (
                                    Number(value) / 100
                                  ).toFixed(2);
                                  field.onChange(formattedValue);
                                }}
                                value={
                                  field.value && !isNaN(Number(field.value))
                                    ? new Intl.NumberFormat("pt-BR", {
                                        style: "currency",
                                        currency: "BRL",
                                      }).format(Number(field.value))
                                    : ""
                                }
                                placeholder="Insira um valor"
                                className="border-gray-200 focus-visible:ring-primary/20"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col gap-2">
                        <FormLabel className="text-sm font-medium text-gray-600">
                          Data da Solicitação de Saldo
                        </FormLabel>
                        <Input
                          type="date"
                          value={
                            cashBalanceByStore?.created_at
                              ? new Date(cashBalanceByStore.created_at)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          disabled
                          className="border-gray-200 focus-visible:ring-primary/20"
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-600">
                            Saldo em Sistema
                          </FormLabel>
                          <Input
                            value={
                              form.watch("systemBalance") !== null &&
                              form.watch("systemBalance") !== undefined
                                ? new Intl.NumberFormat("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  }).format(
                                    Number(form.watch("systemBalance") || 0)
                                  )
                                : ""
                            }
                            disabled
                            className="border-gray-200 focus-visible:ring-primary/20"
                          />
                          <FormMessage />
                        </FormItem>
                      </div>
                    </div>

                    <FormField
                      control={form.control}
                      name="file"
                      render={() => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-600">
                            Conferência de Saldo
                            <p className="text-xs text-gray-500">
                              Selecione e envie o arquivo com a conferência
                            </p>
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="file"
                              accept="application/pdf"
                              onChange={(e) => {
                                const selectedFile = e.target.files?.[0];
                                console.log(
                                  "Arquivo selecionado:",
                                  selectedFile
                                );
                                if (selectedFile) {
                                  form.setValue("file", selectedFile, {
                                    shouldValidate: true,
                                  });
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="hasBalanceDiscrepancy"
                      render={({ field }) => (
                        <FormItem className="bg-gray-50/50 p-4 rounded-lg border border-gray-100">
                          <FormLabel className="text-sm font-medium text-gray-600 mb-2 block">
                            Possui divergência no saldo?
                          </FormLabel>
                          <div className="flex space-x-6">
                            <FormItem className="flex items-center space-x-2">
                              <FormControl>
                                <RadioGroup
                                  onValueChange={(value) =>
                                    field.onChange(value === "true")
                                  }
                                  defaultValue={undefined}
                                  className="flex space-x-6"
                                >
                                  <FormItem className="flex items-center space-x-2">
                                    <RadioGroupItem value="true" id="sim" />
                                    <FormLabel
                                      htmlFor="sim"
                                      className="text-sm text-gray-600"
                                    >
                                      Sim
                                    </FormLabel>
                                  </FormItem>
                                  <FormItem className="flex items-center space-x-2">
                                    <RadioGroupItem value="false" id="nao" />
                                    <FormLabel
                                      htmlFor="nao"
                                      className="text-sm text-gray-600"
                                    >
                                      Não
                                    </FormLabel>
                                  </FormItem>
                                </RadioGroup>
                              </FormControl>
                            </FormItem>
                          </div>
                        </FormItem>
                      )}
                    />

                    {form.watch("hasBalanceDiscrepancy") && (
                      <FormField
                        control={form.control}
                        name="justification"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-600">
                              Justificativa
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Textarea
                                  placeholder="Digite a justificativa para a divergência"
                                  {...field}
                                  maxLength={200}
                                  className="min-h-[100px] border-gray-200 focus-visible:ring-primary/20"
                                />
                                <span className="absolute bottom-2 right-2 text-xs text-gray-500">
                                  {field.value?.length || 0}/200
                                </span>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </CardContent>
                </Card>

                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate(-1)}
                    type="button"
                    className="hidden h-8 lg:flex mb-4"
                    size="sm"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="hidden h-8 lg:flex mb-4"
                    disabled={
                      !cashBalanceByStore ||
                      isFetchingCashBalance ||
                      !!cashBalanceError
                    }
                    size="sm"
                  >
                    Solicitar Termo
                  </Button>
                </div>
              </form>
            </Form>
          </main>
        </div>
      </div>
    </>
  );
};
